/**
 * Estrategia para obtener token directamente desde el backend
 * Implementación modularizada siguiendo principios SOLID y estándares OAuth2 de TiendaNube
 */

import BaseTokenStrategy from './BaseTokenStrategy';

/**
 * Implementa un flujo directo para obtener tokens desde el backend siguiendo
 * el estándar OAuth2 de TiendaNube y RFC 6749/6750 para compatibilidad total
 */
class DirectTokenStrategy extends BaseTokenStrategy {
  /**
   * @param {Object} options - Opciones de configuración
   * @param {string} [options.clientId] - ID de cliente OAuth2
   * @param {string} [options.clientSecret] - Secreto de cliente OAuth2
   * @param {Object} [options.logger] - Logger para diagnóstico (opcional)
   */
  constructor(options = {}) {
    super(options);
    this.clientId = options.clientId;
    this.clientSecret = options.clientSecret;
    this.logger = options.logger || console;
  }
  
  /**
   * Registra un error de forma estandarizada
   * @param {string} context - Contexto del error
   * @param {Error|string} error - Error ocurrido
   * @private
   */
  _logError(context, error) {
    this.logger.error(`[DirectTokenStrategy:${context}]`, error.message || error);
    try {
      localStorage.setItem('direct_token_last_error', JSON.stringify({
        context,
        message: error.message || String(error),
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Error no crítico */}
  }

  /**
   * Obtiene token directamente desde el backend usando el endpoint /api/token
   * @param {Object} user - Usuario Firebase
   * @param {string} tokenToSend - Token Firebase para enviar
   * @param {string} tiendaId - ID de tienda (opcional)
   * @return {Promise<Object|null>} Token normalizado o null
   */
  async execute(user, tokenToSend, tiendaId) {
    try {
      // Registrar información para diagnóstico
      try {
        localStorage.setItem('direct_token_attempt', new Date().toISOString());
        localStorage.setItem('direct_token_user_id', user?.uid || 'unknown');
      } catch (e) {/* Error no crítico */}
      
      // Obtener credenciales OAuth2
      const clientId = this.clientId || 
                      process.env.REACT_APP_API_CLIENT_ID || 
                      window.__API_CLIENT_ID__ || 
                      'nubelytics-web';
      
      const clientSecret = this.clientSecret || 
                          process.env.REACT_APP_API_CLIENT_SECRET || 
                          window.__API_CLIENT_SECRET__ || 
                          'nubelytics-web-client';
      
      // Registrar fuente de credenciales
      try {
        localStorage.setItem('direct_token_credential_source',
          this.clientId ? 'constructor' :
          process.env.REACT_APP_API_CLIENT_ID ? 'env_var' :
          window.__API_CLIENT_ID__ ? 'window_global' : 'default');
      } catch (e) {/* Error no crítico */}

      const appConfig = require('../config/AppConfig').default;
      
      // CORRECCIÓN CRÍTICA: Usar getAuthUrl con la ruta correcta según RFC 6749
      // Los endpoints de OAuth2 deben estar en /auth/, no en /api/
      const endpoint = appConfig.getAuthUrl('/token');
      
      // Registrar endpoint para diagnóstico completo
      try {
        localStorage.setItem('direct_token_url', endpoint);
      } catch (e) {/* Error no crítico */}

      // Preparar headers según RFC 6750 (OAuth 2.0 Bearer Token)
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${tokenToSend}` // Token Firebase como Bearer
      };
      
      // Preparar body con credenciales para TiendaNube
      const requestBody = {
        client_id: clientId,
        client_secret: clientSecret
      };
      
      // Añadir tiendaId si está disponible
      if (tiendaId) {
        requestBody.tienda_id = tiendaId;
      }
      
      // Añadir uid si está disponible
      if (user?.uid) {
        requestBody.uid = user.uid;
      }
      
      // Registrar request para diagnóstico (sanitizado)
      try {
        localStorage.setItem('direct_token_request', JSON.stringify({
          endpoint,
          headers: {...headers, Authorization: 'Bearer [REDACTED]'},
          body: {...requestBody, client_secret: '[REDACTED]'},
          timestamp: new Date().toISOString()
        }));
      } catch (e) {/* Error no crítico */}

      // Realizar petición usando el token Firebase como Bearer
      let response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          credentials: 'include' // Incluir cookies para mantener sesión
        });
        
        // Clonar respuesta para evitar "already read"
        const clonedResponse = response.clone();
        
        // Procesar respuesta exitosa
        if (response.ok) {
          try {
            const data = await response.json();
            const accessToken = data.access_token || data.token;
            
            // Verificar que hay un token válido
            if (accessToken) {
              // Registrar éxito para diagnóstico
              try {
                localStorage.setItem('direct_token_success', 'true');
                localStorage.setItem('direct_token_timestamp', new Date().toISOString());
                localStorage.setItem('direct_token_format', data.token_type || data.format || 'Bearer');
              } catch (e) {/* Error no crítico */}
              
              // Normalizar formato del token según estándar
              return {
                token: accessToken,
                format: data.token_type || data.format || 'Bearer',
                expiresIn: data.expires_in || data.expiresIn || 3600,
                refreshToken: data.refresh_token || data.refreshToken
              };
            } else {
              // Respuesta OK pero sin token válido
              this._logError('missing_token', new Error('Respuesta sin token válido'));
              return null;
            }
          } catch (jsonError) {
            // Error al procesar JSON
            this._logError('json_parse', jsonError);
            return null;
          }
        }
        
        // Procesar error de respuesta
        let errorBody;
        try {
          errorBody = await clonedResponse.json();
        } catch (e) {
          try {
            errorBody = await clonedResponse.text();
          } catch (textError) {
            errorBody = 'No se pudo leer la respuesta';
          }
        }
        
        // Registrar error para diagnóstico
        this._logError('http_error', new Error(`HTTP ${response.status}: ${JSON.stringify(errorBody)}`));
        localStorage.setItem('direct_token_error', JSON.stringify({
          status: response.status,
          statusText: response.statusText,
          body: errorBody,
          timestamp: new Date().toISOString()
        }));
        
      } catch (fetchError) {
        // Error de red/conexión
        this._logError('network', fetchError);
        localStorage.setItem('direct_token_network_error', fetchError.toString());
      }
      
      return null;
    } catch (error) {
      // Error general del método
      this._logError('execute', error);
      return null;
    }
  }
}

export default DirectTokenStrategy;
